import { useConfirmationDialog } from "@/context/ConfirmationDialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ApiError } from "../types";

const API_URL = import.meta.env.VITE_API_URL;

// Types for Burn Batch
interface TokenAmount {
	tokenId: number;
	amount: number;
}

interface BurnBatchDto {
	collectionAddress: string;
	tokenAmounts: TokenAmount[];
}

interface BurnDto {
	collectionAddress: string;
	tokenId: number;
}

interface BurnBatchResponseDto {
	transactionHash: string;
}

export const useBurnBatchMutation = () => {
	const queryClient = useQueryClient();

	return useMutation<BurnBatchResponseDto, ApiError, BurnBatchDto>({
		mutationFn: async (data) => {
			const response = await fetch(`${API_URL}/inventory/burn-batch`, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(data),
			});

			if (!response.ok) {
				const error = await response.json();
				throw error;
			}

			return response.json();
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["nfts"],
			});
			toast.success("Batch burned successfully");
		},
		onError: (error) => {
			toast.error(error.message.slice(0, 300));
		},
	});
};

export const useBurnNFTMutation = () => {
	const queryClient = useQueryClient();
	const { confirm } = useConfirmationDialog();

	return useMutation<BurnBatchResponseDto, ApiError, BurnDto>({
		mutationFn: async (data) => {
			const confirmed = await confirm(
				"Confirm Burn",
				`Are you sure you want to burn #${data.tokenId} NFT?`,
				"Burn",
				"Cancel",
			);

			if (!confirmed) {
				throw new Error("Burn cancelled");
			}

			const response = await fetch(`${API_URL}/inventory/burn`, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(data),
			});

			if (!response.ok) {
				const error = await response.json();
				throw error;
			}

			return response.json();
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["nfts"],
			});
			toast.success("NFT burned successfully");
		},
		onError: (error) => {
			toast.error(error.message.slice(0, 300));
		},
	});
};
